import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of as observableOf, throwError as observableThrowError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AppState } from 'src/store';
import {
  UpsertFlightLogSummary,
  UpsertFlightLogSummarys,
} from 'src/store/flight-log-summary/flight-log-summary.actions';
import { FlightLogSummaryEntity } from 'src/store/flight-log-summary/flight-log-summary.model';
import {
  DeleteFlightLog,
  FlightLogEdited,
  UpsertFlightLog,
} from 'src/store/flight-log/flight-log.actions';
import {
  FlightLogEntity,
  FlightLogFindMyFlightProps,
  FlightLogHighlightsCommonProps,
} from 'src/store/flight-log/flight-log.model';
import { tryGetEndOfDayISO, tryGetStartOfDayISO } from 'src/util/dateLib';
import { assertNotNullOrWhitespace } from 'src/util/values';
import { FlightLogFindMyFlightViewModel } from 'src/view-models/flight-log-find-my-flight-view-model';
import {
  FlightLogSummaryViewModel,
  FlightLogViewModel,
} from 'src/view-models/flight-log-view-model';
import { FlightLogHighlightsViewModel } from '../../../../view-models/flight-log-highlights-view-model';
import { FlightLogApiConfiguration, FLIGHT_LOG_API_CONFIG } from '../flight-log-api-configuration';
import { FlightSummaryDto } from '../flights/flight-client.model';
import { coerceHttpParams } from '../shared/create-request-options';
import { CollectionResult } from './flight-log-client-model-helpers';
import {
  FlightLogQueryParameters,
  FlightLogSummarySortableField,
  LogMode,
} from './flight-log-client.model';

@Injectable({
  providedIn: 'root',
})
export class FlightLogHttpClient {
  constructor(
    @Inject(FLIGHT_LOG_API_CONFIG) private config: FlightLogApiConfiguration,
    private http: HttpClient,
    private _store: Store<AppState>,
  ) {}

  public create(flightLog: FlightLogEntity): Observable<any> {
    return this.http.post(this._url(`flightLogs`), flightLog);
  }

  public createFromFlightSummary(
    flightLogId: string,
    flightSummary: FlightSummaryDto,
    logMode: LogMode,
  ): Observable<any> {
    return this.http.post(this._url(`flightLogs/flightSummary`), {
      flightLogId,
      flightSummary,
      logMode,
    });
  }

  public update(flightLog: FlightLogEntity): Observable<any> {
    return this.http.put(this._url(`flightLogs/${flightLog.id}`), flightLog).pipe(
      tap(() => {
        this._store.dispatch(new UpsertFlightLog({ flightLog }));
      }),
    );
  }

  public begin(flightLogId: string, mode: LogMode): Observable<any> {
    assertNotNullOrWhitespace(flightLogId, 'flightLogId');
    return this.http
      .post(this._url(`flightLogs/${flightLogId}/begin`), {
        flightLogId,
        mode,
      })
      .pipe(
        tap(() => {
          this._store.dispatch(new FlightLogEdited({ id: flightLogId }));
        }),
      );
  }

  public complete(flightLog: FlightLogEntity): Observable<any> {
    return this.http.post(this._url(`flightLogs/${flightLog.id}/complete`), flightLog).pipe(
      tap(() => {
        this._store.dispatch(new UpsertFlightLog({ flightLog }));
      }),
    );
  }

  public reopen(flightLogId: string): Observable<any> {
    assertNotNullOrWhitespace(flightLogId, 'flightLogId');
    return this.http
      .post(this._url(`flightLogs/${flightLogId}/reopen`), {
        flightLogId,
      })
      .pipe(
        tap(() => {
          this._store.dispatch(new FlightLogEdited({ id: flightLogId }));
        }),
      );
  }

  public delete(flightLogId: string): Observable<any> {
    assertNotNullOrWhitespace(flightLogId, 'flightLogId');
    return this.http
      .delete(this._url(`flightLogs/${flightLogId}/`))
      .pipe(tap(() => this._store.dispatch(new DeleteFlightLog({ id: flightLogId }))));
  }

  public restore(flightLogId: string): Observable<any> {
    assertNotNullOrWhitespace(flightLogId, 'flightLogId');
    return this.http
      .post(this._url(`flightLogs/${flightLogId}/restore`), {
        flightLogId,
      })
      .pipe(
        tap(() => {
          this._store.dispatch(new FlightLogEdited({ id: flightLogId }));
        }),
      );
  }

  public getById(flightLogId: string): Observable<FlightLogViewModel | undefined> {
    assertNotNullOrWhitespace(flightLogId, 'flightLogId');
    // Temporary until we can re-import FL data from PST
    // as previously broken import caused some ac ids to be lower case
    function fixAcId(fl: FlightLogEntity): FlightLogEntity {
      if (fl != null && fl.aircraftId != null) {
        fl.aircraftId = fl.aircraftId.toUpperCase();
      }
      return fl;
    }

    return this.http.get<FlightLogEntity | undefined>(this._url(`flightLogs/${flightLogId}`)).pipe(
      map((obj) => (obj != null ? fixAcId(obj) : undefined)),
      tap((flightLog) => {
        this._store.dispatch(
          flightLog != null
            ? new UpsertFlightLog({ flightLog })
            : new DeleteFlightLog({ id: flightLogId }),
        );
      }),
      map((fl) => (fl != null ? new FlightLogViewModel(fl) : undefined)),
    );
  }

  public getPreviousFlightLog(
    aircraftId: string,
    flightTime: Date,
  ): Observable<FlightLogSummaryViewModel | undefined> {
    return this.http
      .get<FlightLogSummaryEntity | undefined>(this._url(`flightLogs/previous`), {
        params: {
          aircraftId,
          flightTime: flightTime.toISOString(),
        },
      })
      .pipe(
        catchError((r) => {
          if (r.status === 404) {
            return observableOf(undefined as FlightLogSummaryEntity | undefined);
          } else {
            return observableThrowError(r);
          }
        }),
        tap((fl) => {
          if (fl != null) {
            this._store.dispatch(new UpsertFlightLogSummary({ flightLogSummary: fl }));
          }
        }),
        map((fl) => (fl != null ? new FlightLogSummaryViewModel(fl) : undefined)),
      );
  }

  public getNextFlightLog(
    aircraftId: string,
    flightTime: Date,
  ): Observable<FlightLogSummaryViewModel | undefined> {
    return this.http
      .get<FlightLogSummaryEntity | undefined>(this._url(`flightLogs/next`), {
        params: {
          aircraftId,
          flightTime: flightTime.toISOString(),
        },
      })
      .pipe(
        catchError((r) => {
          if (r.status === 404) {
            return observableOf(undefined as FlightLogSummaryEntity | undefined);
          } else {
            return observableThrowError(r);
          }
        }),
        tap((fl) => {
          if (fl != null) {
            this._store.dispatch(new UpsertFlightLogSummary({ flightLogSummary: fl }));
          }
        }),
        map((fl) => (fl != null ? new FlightLogSummaryViewModel(fl) : undefined)),
      );
  }

  public isActive(flightLogId: string): Observable<boolean> {
    return this.getById(flightLogId).pipe(map((fl) => fl == null || fl.deletedAt == null));
  }

  public getMany({
    employeeId,
    dispatchId,
    tailNumber,
    startTime,
    endTime,
    currentBaseId,
    currentAircraftId,
    flightTagNumber,
    companyId,
    deletedOnly,
    sort: orderBy,
    limit,
    openOnly,
    skip,
  }: FlightLogQueryParameters): Observable<CollectionResult<FlightLogSummaryViewModel>> {
    const queryParams = this.getFlightLogQueryParams({
      employeeId,
      dispatchId,
      tailNumber,
      startTime,
      endTime,
      currentBaseId,
      currentAircraftId,
      flightTagNumber,
      companyId,
      deletedOnly,
      sort: orderBy,
      limit,
      openOnly,
      skip,
    });

    return this.http
      .get<CollectionResult<FlightLogSummaryEntity>>(this._url(`flightLogs`), {
        params: coerceHttpParams(queryParams),
      })
      .pipe(
        tap(({ results }) => {
          this._store.dispatch(new UpsertFlightLogSummarys({ flightLogSummarys: results }));
        }),
        map(({ results, totalCount }) => ({
          results: results.map((r) => new FlightLogSummaryViewModel(r)),
          totalCount,
        })),
      );
  }

  public getManyWithHighlights({
    employeeId,
    dispatchId,
    tailNumber,
    startTime,
    endTime,
    currentBaseId,
    currentAircraftId,
    flightTagNumber,
    companyId,
    deletedOnly,
    sort: orderBy,
    limit,
    openOnly,
    skip,
  }: FlightLogQueryParameters): Observable<CollectionResult<FlightLogHighlightsViewModel>> {
    const queryParams = this.getFlightLogQueryParams({
      employeeId,
      dispatchId,
      tailNumber,
      startTime,
      endTime,
      currentBaseId,
      currentAircraftId,
      flightTagNumber,
      companyId,
      deletedOnly,
      sort: orderBy,
      limit,
      openOnly,
      skip,
    });

    return this.http
      .get<CollectionResult<FlightLogHighlightsCommonProps>>(
        this._url(`flightLogsWithHighlights`),
        {
          params: coerceHttpParams(queryParams),
        },
      )
      .pipe(
        map(({ results, totalCount }) => ({
          results: results.map((r) => new FlightLogHighlightsViewModel(r)),
          totalCount,
        })),
      );
  }

  public getManyFindMyFlight(
    dispatchId: string,
    companyId: string,
  ): Observable<CollectionResult<FlightLogFindMyFlightViewModel>> {
    return this.http
      .get<CollectionResult<FlightLogFindMyFlightProps>>(this._url(`flightLogs/findMyFlight`), {
        params: { dispatchId, companyId },
      })
      .pipe(
        map(({ results, totalCount }) => ({
          results: results.map((r) => new FlightLogFindMyFlightViewModel(r)),
          totalCount,
        })),
      );
  }

  private _url(path: string) {
    return this.config.baseUri + '/api/' + path;
  }

  private getFlightLogQueryParams({
    employeeId,
    dispatchId,
    tailNumber,
    startTime,
    endTime,
    currentBaseId,
    currentAircraftId,
    flightTagNumber,
    companyId,
    deletedOnly,
    sort: orderBy,
    limit,
    openOnly,
    skip,
  }: FlightLogQueryParameters) {
    let orderByField: string | undefined;
    let $orderBy: string | undefined;
    if (orderBy != null) {
      const sortableFieldMapping: { [key in FlightLogSummarySortableField]?: string } = {
        tailNumber: '[aircraft.tailNumber]',
        aircraftMake: '[aircraft.make]',
        aircraftModel: '[aircraft.model]',
        aircraftType: '[aircraft.aircraftType]',
      };
      const customFieldName = sortableFieldMapping[orderBy.orderBy];
      orderByField = customFieldName != null ? customFieldName : orderBy.orderBy;
      if (orderByField != null) {
        $orderBy = `${orderByField} ${orderBy.direction.toUpperCase()}`;
      }
    }

    const queryParams: {
      pilotEmployeeId?: string;
      dispatchId?: string;
      tailNumber?: string;
      startTime?: string;
      endTime?: string;
      open?: string;
      pilotBaseId?: string;
      pilotAircraftId?: string;
      flightTagNumber?: string;
      companyId?: string;
      deletedOnly?: string;
      $orderBy?: string;
      limit?: string;
      skip?: string;
      envelope: 'true';
    } = {
      open: openOnly === true ? 'true' : undefined,
      skip: skip != null ? String(skip) : undefined,
      limit: limit != null ? String(limit) : '10',
      pilotEmployeeId: employeeId,
      dispatchId,
      startTime: tryGetStartOfDayISO(startTime),
      endTime: tryGetEndOfDayISO(endTime),
      $orderBy,
      pilotBaseId: currentBaseId,
      pilotAircraftId: currentAircraftId,
      flightTagNumber,
      tailNumber,
      companyId,
      deletedOnly: deletedOnly != null ? String(deletedOnly) : undefined,
      envelope: 'true',
    };

    return queryParams;
  }
}
